import React from "react";
import { supabase } from "../lib/supabase";
import { SLUG_ATLAS, useBranding, type Branding } from "../branding";

// El halo de la marca: tres manchas de luz con los colores de la marca, que se apagan hacia abajo
// (ADR-2026-10-02-mi-marca-halo-de-marca). Nació en la cabecera de Mi marca; vive acá desde que
// Tienda online lo usa también (ADR-2026-10-29-tienda-online-refleja-la-tienda): una sola regla de
// qué colores se eligen, para que las dos pantallas pinten la misma marca igual.

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** `#abc` → `#aabbcc`, para poder sumarle un canal alfa como sufijo. */
export const hex6 = (hex: string): string => {
  const h = hex.replace("#", "");
  return `#${h.length === 3 ? h.split("").map((c) => c + c).join("") : h}`;
};

/** Los hex válidos de `brand_profiles.colors`. */
export const paletaDeColores = (colors: unknown): string[] =>
  (Array.isArray(colors) ? (colors as { hex?: string }[]) : [])
    .map((c) => (c?.hex || "").trim())
    .filter((h) => HEX.test(h));

/**
 * Los colores del halo: los tres más saturados de la paleta, sin los casi negros ni casi blancos (un
 * halo gris no dice nada de la marca). Sin ninguno que sirva, los de la app: los azules de Atlas, o
 * el primario y el acento del branding.
 */
export const coloresDelHalo = (paleta: string[], branding: Branding): [string, string, string] => {
  const utiles = paleta
    .map((hex) => {
      const h = hex6(hex);
      const [r, g, b] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const l = (max + min) / 2;
      const sat = max === min ? 0 : (max - min) / (1 - Math.abs(2 * l - 1));
      return { h, sat, l };
    })
    .filter((c) => c.sat > 0.2 && c.l > 0.1 && c.l < 0.92)
    .sort((a, b) => b.sat - a.sat)
    .map((c) => c.h);
  if (utiles.length === 0) {
    // Sin colores propios: los de la app. Atlas conserva sus azules del halo; un cliente con
    // marca blanca usa su primario y su acento (ADR-2026-10-26-los-tintes-de-marca-siguen-al-branding).
    if (branding.slug === SLUG_ATLAS) return ["#3D5CFF", "#8EA2FF", "#C9D3FF"]; // hex-de-marca-ok: el halo de Atlas, a propósito
    const { primary, accent } = branding.colores;
    return [primary, accent, primary];
  }
  return [utiles[0]!, utiles[1] ?? utiles[0]!, utiles[2] ?? utiles[1] ?? utiles[0]!];
};

const MASCARA_DEL_HALO =
  "linear-gradient(to bottom, black 35%, transparent), linear-gradient(to right, transparent, black 14%, black 86%, transparent)";
/**
 * Abajo de `lg` arriba del halo está el título de la sección (`MobilePageTitle`), no el borde de la
 * página: el halo entra en degradé también desde arriba. Empezando a pleno, tapaba el título y lo
 * cortaba con una línea recta — ADR-2026-10-05-mi-marca-cabecera-en-mobile.
 */
const MASCARA_DEL_HALO_MOBILE =
  "linear-gradient(to bottom, transparent, black 30%, black 45%, transparent), linear-gradient(to right, transparent, black 14%, black 86%, transparent)";

/**
 * La paleta de una marca, para quien no la tiene a mano (Mi marca la lee con el resto de sus datos).
 * Se relee con `atlas:brand-changed`, que se emite al guardar los colores. Se devuelve sólo para la
 * marca de la que vino: al cambiar de marca no se ve la paleta de la anterior.
 */
export function usePaletaDeMarca(brandId: string | null): string[] {
  const [leida, setLeida] = React.useState<{ brandId: string; paleta: string[] } | null>(null);
  React.useEffect(() => {
    if (!brandId) return;
    let cancelado = false;
    const leer = async () => {
      const { data, error } = await supabase.from("brand_profiles").select("colors").eq("id", brandId).maybeSingle();
      if (!cancelado && !error) setLeida({ brandId, paleta: paletaDeColores(data?.colors) });
    };
    void leer();
    window.addEventListener("atlas:brand-changed", leer);
    return () => {
      cancelado = true;
      window.removeEventListener("atlas:brand-changed", leer);
    };
  }, [brandId]);
  return leida && leida.brandId === brandId ? leida.paleta : [];
}

/**
 * El halo, posicionado detrás de lo que lo contiene: el padre tiene que ser `relative isolate`. Sale
 * 32px a los costados y arriba —la sangría del shell— y se desvanece con dos máscaras intersectadas
 * (hacia abajo Y hacia los costados) en vez de terminar en línea. Aparece suave al cambiar de paleta.
 */
export function HaloDeMarca({ paleta, escritorio }: { paleta: string[]; escritorio: boolean }) {
  const branding = useBranding();
  // Sufijos de alfa en hex (~22%, 18%, 20%): tiene que teñir, no competir con el contenido.
  const [c1, c2, c3] = coloresDelHalo(paleta, branding);
  const halo = [
    `radial-gradient(46% 100% at 4% 0%, ${c1}38 0%, transparent 70%)`,
    `radial-gradient(40% 85% at 42% 0%, ${c2}2e 0%, transparent 70%)`,
    `radial-gradient(44% 95% at 92% 0%, ${c3}33 0%, transparent 70%)`,
  ].join(", ");
  return (
    <div
      key={paleta.join(",") || "sin-paleta"}
      aria-hidden
      className="pointer-events-none absolute -inset-x-4 top-0 -z-10 h-[260px] sm:-inset-x-8 lg:-top-8 lg:h-[300px] animate-[aparecer-suave_0.8s_ease-out_both]"
      style={{
        background: halo,
        WebkitMaskImage: escritorio ? MASCARA_DEL_HALO : MASCARA_DEL_HALO_MOBILE,
        maskImage: escritorio ? MASCARA_DEL_HALO : MASCARA_DEL_HALO_MOBILE,
        WebkitMaskComposite: "source-in",
        maskComposite: "intersect",
      }}
    />
  );
}
